import { traducirError, useTraduccion } from '@tallerya/i18n';
import type { ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';
import { usePermisos } from './usePermiso';

// La guarda de una ruta: decide si se dibuja lo de adentro.
//
// **Ocultar no es proteger.** Esto es comodidad para quien mira la pantalla; la
// comprobación real corre en el servidor. Una ruta sin guarda no es un agujero de
// seguridad, es una pantalla que se va a ver vacía o llena de errores 403.
//
// ## Los cuatro estados, y por qué ninguno sobra
//
//   resolviendo   la sesión todavía no se sabe. Pasa al recargar la página,
//                 mientras oidc-client lee sessionStorage
//   sin sesión    nunca entró, o se le venció y no se pudo renovar
//   sin permiso   entró, y esto no le toca
//   adentro       se dibuja
//
// Saltarse el primero es el error clásico: la pantalla muestra «entrá» durante un
// instante y después el contenido. Quien lo ve cree que la sesión se perdió.

interface Props {
  /// El permiso que exige esta ruta. Sin él, alcanza con estar dentro del taller.
  ///
  /// Se escribe con el código del backend —`order.close`— y **un código que no
  /// existe deja la ruta cerrada para siempre**, sin que nada lo avise.
  permiso?: string;
  children: ReactNode;
}

export function Guardia({ permiso, children }: Props) {
  const { t } = useTraduccion();
  const auth = useAuth();
  const permisos = usePermisos();

  if (auth.isLoading || permisos.cargando) {
    return <p className="entrada">{t('app.cargando')}</p>;
  }

  if (!auth.isAuthenticated) {
    return (
      <section className="aviso">
        <h2>{t('admin.guardia.sinSesion.titulo')}</h2>
        <p>{t('admin.guardia.sinSesion.texto')}</p>
        <button type="button" onClick={() => void auth.signinRedirect()}>
          {t('admin.sesion.entrar')}
        </button>
      </section>
    );
  }

  // El taller está suspendido (#243). Va antes que el fallo genérico porque este
  // no se arregla volviendo a entrar: decirle eso al dueño lo manda a dar vueltas.
  // El texto es el mismo que manda la API, desde su entrada en el catálogo. Con
  // el admin abierto también aparece, y se va solo al reactivarlo (#244).
  if (permisos.suspendido) {
    return (
      <section className="aviso">
        <h2>{t('admin.guardia.suspendido.titulo')}</h2>
        <p>{traducirError('auth.tenant_suspended', t)}</p>
      </section>
    );
  }

  // La consulta falló. El caso que importa es el `403` de quien ya no pertenece
  // al taller: su token sigue vivo, así que para el navegador la sesión es
  // válida, pero del lado del servidor ya no es de nadie.
  if (permisos.error) {
    return (
      <section className="aviso">
        <h2>{t('admin.guardia.error.titulo')}</h2>
        <p>{t('admin.guardia.error.texto')}</p>
      </section>
    );
  }

  if (permiso && !permisos.puede(permiso)) {
    return (
      <section className="aviso">
        <h2>{t('admin.guardia.sinPermiso.titulo')}</h2>
        <p>{t('admin.guardia.sinPermiso.texto')}</p>
      </section>
    );
  }

  return <>{children}</>;
}
